<script setup lang="ts">
/**
 * «С прошлой встречи» — блок 12-7 (эпик DEV-2088, решение 9 гриля).
 *
 * Серия — это один разговор, растянутый на недели, и начинается он не с
 * чистого листа: неразобранные действия прошлого созвона и заведённые из них
 * открытые задачи стоят в повестке следующего.
 *
 * Только нерешённое: о сделанном напоминать незачем, а закрытая задача в
 * напоминании читается как «сделай ещё раз».
 */
import { ChevronDown, ChevronRight } from 'lucide-vue-next'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { formatDueDate } from '@/features/issues/format'

import type { MeetingCarryOver } from '../api'

const props = defineProps<{
  carryOver: MeetingCarryOver
  /** Когда был прошлый созвон — датой блок и подписан */
  previousAt: string
}>()

const { t, locale } = useI18n()

const isOpen = ref(true)

const when = computed(() => (props.previousAt ? formatDueDate(props.previousAt, locale.value) : ''))

const total = computed(() => props.carryOver.actions.length + props.carryOver.openIssues.length)

/** Срок, который называли в прошлый раз: «было: до 2 сент.» */
const wasDue = (iso: string) => t('meetings.carry.was', { date: formatDueDate(iso, locale.value) })
</script>

<template>
  <section v-if="total" class="carry">
    <button type="button" class="carry__head" @click="isOpen = !isOpen">
      <ChevronDown v-if="isOpen" :size="14" aria-hidden="true" />
      <ChevronRight v-else :size="14" aria-hidden="true" />

      <span class="carry__title">{{ t('meetings.carry.title') }}</span>
      <span v-if="when" class="carry__when">{{ when }}</span>
    </button>

    <template v-if="isOpen">
      <p v-for="action in carryOver.actions" :key="action.id" class="carry__row">
        <span class="carry__text">{{ action.body }}</span>
        <span v-if="action.ai?.dueDate" class="carry__chip">{{ wasDue(action.ai.dueDate) }}</span>
      </p>

      <template v-if="carryOver.openIssues.length">
        <p class="carry__group">{{ t('meetings.carry.openIssues') }}</p>

        <p v-for="issue in carryOver.openIssues" :key="issue.key" class="carry__row">
          <RouterLink class="carry__key" :to="`/issue/${issue.key}`">{{ issue.key }}</RouterLink>
          <span class="carry__text">{{ issue.title }}</span>
          <span class="carry__chip">{{ issue.columnName }}</span>
        </p>
      </template>

      <!-- Прошлый конспект — ссылкой, а не текстом: он длиннее всей повестки
           вместе взятой, и место ему на своей встрече. -->
      <RouterLink
        v-if="carryOver.previousSessionId"
        class="carry__link"
        :to="`/meetings/${carryOver.previousSessionId}`"
      >
        {{ t('meetings.carry.openSummary') }}
      </RouterLink>
    </template>
  </section>
</template>

<style scoped>
.carry {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-muted);
}

.carry__head {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-primary);
  font-family: inherit;
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-medium);
  line-height: 18px;
  text-align: left;
  cursor: pointer;
}

.carry__when,
.carry__group {
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-regular);
  line-height: 16px;
}

.carry__group {
  margin: 4px 0 0;
}

.carry__row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  margin: 0;
  font-size: var(--font-size-caption);
  line-height: 18px;
}

.carry__text {
  color: var(--color-text-secondary);
}

.carry__key {
  color: var(--color-primary);
  text-decoration: none;
}

.carry__chip {
  padding: 0 6px;
  border-radius: var(--radius-pill, 999px);
  background: var(--color-surface);
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
  line-height: 18px;
}

.carry__link {
  align-self: flex-start;
  margin-top: 2px;
  color: var(--color-primary);
  font-size: var(--font-size-caption);
  line-height: 18px;
  text-decoration: none;
}
</style>
